import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api } from "~/data/core/api";
import { socket } from "~/data/core/socket";
import { sendChatMessage } from "~/data/user-test/user-test.service";

import { useUserTest } from "./queries/use-user-test";
import { useMediaStream } from "../shared/utils/use-media-stream";

const DEFAULT_STT_DEV_PORT = "5000";
const PCM_WORKLET_URL = new URL("./worklets/pcm-worklet.js", import.meta.url);
const TARGET_SAMPLE_RATE = 48_000;
const ELEVENLABS_STT_ENABLED =
  (import.meta.env.VITE_ELEVENLABS_STT_ENABLED ?? "false") !== "false";
const ELEVENLABS_STT_MODEL =
  import.meta.env.VITE_ELEVENLABS_STT_MODEL ?? "scribe_v2_realtime";
const ELEVENLABS_STT_URL =
  import.meta.env.VITE_ELEVENLABS_STT_URL ??
  "wss://api.elevenlabs.io/v1/speech-to-text/realtime";
const ELEVENLABS_SAMPLE_RATE = Number(
  import.meta.env.VITE_ELEVENLABS_SAMPLE_RATE ?? "16000"
);
const STT_PROVIDER = (
  import.meta.env.VITE_STT_PROVIDER ??
  (ELEVENLABS_STT_ENABLED ? "elevenlabs" : "browser")
).toLowerCase();

type PlaybackHandle = {
  context: AudioContext;
  source: AudioBufferSourceNode;
};

type BrowserSpeechRecognitionEvent = Event & {
  results: ArrayLike<{
    0: { transcript: string };
  }>;
};

type BrowserSpeechRecognitionErrorEvent = Event & {
  error: string;
};

type MinimalSpeechRecognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
  onerror: ((event: BrowserSpeechRecognitionErrorEvent) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
};

type SpeechRecognitionCtor = new () => MinimalSpeechRecognition;

type SpeechRecognitionWindow = Window &
  typeof globalThis & {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };

export function Talk() {
  const params = useParams();
  const waveformRef = useRef<HTMLCanvasElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const playbackRef = useRef<PlaybackHandle | null>(null);
  const playbackSignalRef = useRef<AbortSignal | null>(null);
  const realtimeFallbackRef = useRef(false);
  const realtimePendingRef = useRef(false);
  const currentRealtimeTextRef = useRef<string | null>(null);
  const pendingRealtimeQueueRef = useRef<string[]>([]);

  const visibilityState = useVisibilityState();

  const userTest = useUserTest(params.id!);
  const started = !!userTest.started_at;
  const finished = !!userTest.finished_at;

  const mediaStreamController = useMediaStream();
  const playStream = mediaStreamController.play;
  const stopStream = mediaStreamController.stop;
  useEffect(() => {
    if (!started || finished) {
      stopStream();
      return;
    }

    playStream();
    return () => stopStream();
  }, [finished, started, playStream, stopStream]);

  const transcriptionAbortRef = useRef<AbortController | null>(null);
  const shouldStream =
    !finished && started && visibilityState === "visible" && Boolean(params.id);
  const patientChannelIdentifier = useMemo(
    () => (params.id ? socket.channels.patientSimulation(params.id) : null),
    [params.id]
  );
  const realtimeEligible = Boolean(patientChannelIdentifier && started && !finished);

  const stopPlayback = useCallback(() => {
    if (!playbackRef.current) return;
    const handle = playbackRef.current;
    playbackRef.current = null;
    try {
      handle.source.onended = null;
      handle.source.stop();
    } catch {
      // noop
    }
    handle.context.close().catch(() => {});
  }, []);

  const playBase64Audio = useCallback(
    async (base64: string, signal?: AbortSignal | null) => {
      if (!base64) return;
      console.log("[WS] Recebido áudio base64 da IA", {
        length: base64.length,
        signalAborted: signal?.aborted ?? false,
      });
      stopPlayback();
      try {
        const handle = await startPlaybackFromBase64(base64, waveformRef.current);
        if (!handle) return;
        if (signal?.aborted) {
          handle.source.stop();
          handle.context.close().catch(() => {});
          return;
        }
        playbackRef.current = handle;
        handle.source.onended = () => {
          handle.context.close().catch(() => {});
          if (playbackRef.current === handle) {
            playbackRef.current = null;
          }
        };
      } catch (error) {
        console.error("[STT] Falha ao reproduzir áudio da IA", error);
      }
    },
    [stopPlayback]
  );

  const sendTextViaHttp = useCallback(
    async (text: string, signal?: AbortSignal | null) => {
      console.log("[STT] IA via fallback HTTP");
      const responseData = await sendChatMessage(params.id!, text);
      console.log("[STT] IA respondeu (texto):", responseData.response);

      const base64 = responseData.audio;
      if (!base64) {
        console.warn("[STT] Resposta sem áudio; nada para reproduzir.");
        return;
      }

      await playBase64Audio(base64, signal);
    },
    [params.id, playBase64Audio]
  );

  const scheduleRealtimeTurn = useCallback(
    (text: string) => {
      if (!patientChannelIdentifier || realtimeFallbackRef.current) return false;

      if (realtimePendingRef.current) {
        pendingRealtimeQueueRef.current.push(text);
        console.log("[STT] Turno enfileirado aguardando resposta:", text);
        return true;
      }

      try {
        socket.send({
          command: "message",
          identifier: patientChannelIdentifier,
          data: { type: "user_turn", text },
        });
        realtimePendingRef.current = true;
        currentRealtimeTextRef.current = text;
        return true;
      } catch (error) {
        console.error("[STT] Falha ao enviar via Realtime", error);
        return false;
      }
    },
    [patientChannelIdentifier]
  );

  useEffect(() => {
    if (!patientChannelIdentifier || !realtimeEligible) return;
    const unsubscribe = socket.subscribe(patientChannelIdentifier);
    realtimeFallbackRef.current = false;
    return () => unsubscribe?.();
  }, [patientChannelIdentifier, realtimeEligible]);

  useEffect(() => {
    if (!patientChannelIdentifier || !realtimeEligible) return;

    const matches = (raw?: { identifier?: string }) =>
      messageMatchesIdentifier(raw, patientChannelIdentifier);

    const offDelta = socket.on("delta", (payload, raw) => {
      if (!matches(raw)) return;
      if (payload?.text) {
        console.debug("[WS] Transcrição parcial da IA:", payload.text);
      }
    });

    const offDone = socket.on("done", (payload, raw) => {
      if (!matches(raw)) return;
      console.debug("[WS] Resposta da IA finalizada", payload);
      realtimePendingRef.current = false;
      currentRealtimeTextRef.current = null;

      const next = pendingRealtimeQueueRef.current.shift();
      if (next) {
        const sent = scheduleRealtimeTurn(next);
        if (!sent) {
          realtimeFallbackRef.current = true;
          const signal = playbackSignalRef.current;
          const texts = [next, ...pendingRealtimeQueueRef.current];
          pendingRealtimeQueueRef.current = [];
          (async () => {
            for (const text of texts) {
              await sendTextViaHttp(text, signal ?? undefined);
            }
          })();
        }
      }
    });

    const offTts = socket.on("tts", (payload, raw) => {
      if (!matches(raw)) return;
      if (!payload?.audio_base64) return;
      console.log("[WS] Evento TTS recebido", {
        context: payload?.context ?? "response",
        mime: payload?.mime,
        base64Length: payload.audio_base64.length,
      });
      const signal = playbackSignalRef.current;
      playBase64Audio(payload.audio_base64, signal);
    });

    const offError = socket.on("error", (payload, raw) => {
      if (!matches(raw)) return;
      realtimeFallbackRef.current = true;
      realtimePendingRef.current = false;
      const signal = playbackSignalRef.current;

      const pendingTexts: string[] = [];
      if (currentRealtimeTextRef.current) {
        pendingTexts.push(currentRealtimeTextRef.current);
      }
      currentRealtimeTextRef.current = null;
      if (pendingRealtimeQueueRef.current.length) {
        pendingTexts.push(...pendingRealtimeQueueRef.current);
      }
      pendingRealtimeQueueRef.current = [];
      const message =
        payload?.error?.message ??
        (typeof payload?.error === "string"
          ? payload.error
          : "Erro no fluxo em tempo real.");
      toast.error(message, { duration: 8_000 });
      console.error("[WS] Erro no fluxo em tempo real:", message, payload);
      if (payload?.audio_base64) {
        console.log("[WS] Tocando áudio de erro emitido pelo backend");
        playBase64Audio(payload.audio_base64, signal);
      }
      (async () => {
        for (const text of pendingTexts) {
          await sendTextViaHttp(text, signal ?? undefined);
        }
      })();
    });

    return () => {
      offDelta();
      offDone();
      offTts();
      offError();
    };
  }, [
    patientChannelIdentifier,
    playBase64Audio,
    realtimeEligible,
    scheduleRealtimeTurn,
    sendTextViaHttp,
  ]);

  useEffect(() => {
    console.info("[STT] Effect evaluation", {
      shouldStream,
      started,
      finished,
      visibilityState,
    });

    if (!shouldStream) {
      transcriptionAbortRef.current?.abort();
      transcriptionAbortRef.current = null;
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;
    transcriptionAbortRef.current = abortController;

    const setupFn = getSttSetupFn();

    playbackSignalRef.current = signal;

    setupFn({
      async getAudioStream() {
        return navigator.mediaDevices.getUserMedia({ audio: true });
      },
      async onTranscription(finalTranscriptionText) {
        console.log("[STT] Enviando transcrição para IA:", finalTranscriptionText);
        if (scheduleRealtimeTurn(finalTranscriptionText)) {
          console.log("[STT] Turno enviado via WebSocket Realtime");
          return;
        }

        await sendTextViaHttp(finalTranscriptionText, signal);
      },
      signal,
    });

    return () => {
      console.info("[STT] Cleaning up transcription effect");
      abortController.abort();
      stopPlayback();
      if (playbackSignalRef.current === signal) {
        playbackSignalRef.current = null;
      }
      realtimePendingRef.current = false;
      currentRealtimeTextRef.current = null;
      pendingRealtimeQueueRef.current = [];
      if (transcriptionAbortRef.current === abortController) {
        transcriptionAbortRef.current = null;
      }
    };
  }, [
    shouldStream,
    params.id,
    finished,
    started,
    visibilityState,
    playBase64Audio,
    scheduleRealtimeTurn,
    stopPlayback,
    sendTextViaHttp,
  ]);

  return (
    <>
      <div
        className="p-3 flex flex-col gap-3"
        style={{
          borderRadius: "10px",
          background:
            "linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), var(--Secondary-1, #00C2CB)",
        }}
      >
        <video
          ref={mediaStreamController.ref}
          autoPlay
          muted
          playsInline
          className="h-44 rounded-xl w-72 bg-gray-800"
        />
        <audio ref={audioRef} className="h-0 opacity-0" />
        <canvas ref={waveformRef} className="bg-gray-800 w-72 h-44 rounded-xl" />
      </div>
      {finished && (
        <p className="text-sm text-gray-600 mt-2 max-w-[18rem]">
          Prática encerrada — a IA não recebe mais áudio nem responde nesta etapa.
        </p>
      )}
    </>
  );
}

function useVisibilityState() {
  const [state, setState] = useState<DocumentVisibilityState>(
    document.visibilityState
  );
  useEffect(() => {
    const controller = new AbortController();
    document.addEventListener(
      "visibilitychange",
      () => setState(document.visibilityState),
      { signal: controller.signal }
    );
    return () => controller.abort();
  }, []);
  return state;
}

interface SetupTranscriptionOptions {
  onTranscription(text: string): void | Promise<void>;
  getAudioStream(): Promise<MediaStream>;
  signal: AbortSignal;
}
async function setupElevenLabsTranscription({
  signal,
  ...options
}: SetupTranscriptionOptions) {
  const transcriptionSegments: string[] = [];

  let mediaStream: MediaStream;
  try {
    mediaStream = await options.getAudioStream();
  } catch (error) {
    console.error("[STT] Não foi possível acessar o microfone", error);
    return;
  }

  let audioContext: AudioContext;
  try {
    audioContext = new AudioContext({ sampleRate: ELEVENLABS_SAMPLE_RATE });
  } catch {
    audioContext = new AudioContext();
  }

  try {
    await audioContext.audioWorklet.addModule(PCM_WORKLET_URL);
  } catch (error) {
    console.error("[STT] Falha ao carregar AudioWorklet", error);
    mediaStream.getTracks().forEach((track) => track.stop());
    return;
  }

  let token: string | undefined;
  try {
    const response = await api.post("/api/v1/elevenlabs/stt_token", {
      model_id: ELEVENLABS_STT_MODEL,
    });
    token = response.data?.token;
    if (!token) throw new Error("Resposta ElevenLabs sem token");
  } catch (error) {
    console.error("[STT] Não foi possível obter token ElevenLabs", error);
    mediaStream.getTracks().forEach((track) => track.stop());
    return;
  }

  const wsUrl = new URL(ELEVENLABS_STT_URL);
  wsUrl.searchParams.set("model_id", ELEVENLABS_STT_MODEL);
  if (token) {
    wsUrl.searchParams.set("token", token);
  }

  const conn = new WebSocket(wsUrl.toString());
  let source: MediaStreamAudioSourceNode | null = null;
  let pcmNode: AudioWorkletNode | null = null;
  let silenceGain: GainNode | null = null;
  let audioContextClosed = false;
  let microphoneStopped = false;
  let uncommittedSamples = 0;
  let chunkSequence = 0;

  const stopMicrophone = () => {
    if (microphoneStopped) return;
    microphoneStopped = true;
    mediaStream.getTracks().forEach((track) => track.stop());
  };

  const teardownAudioPipeline = () => {
    if (pcmNode) {
      pcmNode.port.onmessage = null;
      pcmNode.disconnect();
      pcmNode = null;
    }
    if (silenceGain) {
      silenceGain.disconnect();
      silenceGain = null;
    }
    if (source) {
      source.disconnect();
      source = null;
    }
    if (!audioContextClosed && audioContext.state !== "closed") {
      audioContextClosed = true;
      audioContext.close().catch((error) => {
        console.warn("[STT] Erro ao encerrar AudioContext", error);
      });
    }
    stopMicrophone();
  };

  const sendChunkToElevenLabs = (arrayBuffer: ArrayBuffer) => {
    if (conn.readyState !== WebSocket.OPEN) return;
    const base64Audio = arrayBufferToBase64(arrayBuffer);
    chunkSequence += 1;

    const samplesInChunk = arrayBuffer.byteLength / Int16Array.BYTES_PER_ELEMENT;
    uncommittedSamples += samplesInChunk;

    const minCommitSamples = ELEVENLABS_SAMPLE_RATE * 0.35; // ~350ms
    const shouldCommit = uncommittedSamples >= minCommitSamples;

    const payload = {
      message_type: "input_audio_chunk",
      audio_base_64: base64Audio,
      sample_rate: ELEVENLABS_SAMPLE_RATE,
      commit: shouldCommit,
    };

    if (import.meta.env.DEV && (shouldCommit || chunkSequence % 20 === 0)) {
      console.debug("[STT][DEV] Enviando chunk ElevenLabs", {
        seq: chunkSequence,
        bytes: arrayBuffer.byteLength,
        commit: shouldCommit,
        secondsPending: (uncommittedSamples / ELEVENLABS_SAMPLE_RATE).toFixed(2),
      });
    }

    conn.send(JSON.stringify(payload));

    if (shouldCommit) {
      uncommittedSamples = 0;
    }
  };

  conn.onopen = async () => {
    console.info("[STT] ElevenLabs WebSocket conectado");

    try {
      await audioContext.resume();
    } catch (error) {
      console.warn("[STT] Não foi possível retomar o AudioContext", error);
    }

    source = audioContext.createMediaStreamSource(mediaStream);
    pcmNode = new AudioWorkletNode(audioContext, "pcm-worklet");
    silenceGain = audioContext.createGain();
    silenceGain.gain.value = 0;

    pcmNode.port.onmessage = (event) => {
      const buffer = event.data as ArrayBuffer;
      if (!buffer) return;
      if (import.meta.env.DEV) {
        logDevRms(new Int16Array(buffer));
      }
      sendChunkToElevenLabs(buffer);
    };

    source.connect(pcmNode);
    pcmNode.connect(silenceGain);
    silenceGain.connect(audioContext.destination);
  };

  conn.onerror = (event) => {
    console.error("[STT] Erro na conexão ElevenLabs", event);
    teardownAudioPipeline();
  };

  conn.onclose = (event) => {
    console.warn("[STT] Conexão ElevenLabs encerrada", {
      code: event.code,
      reason: event.reason || "(sem motivo)",
      wasClean: event.wasClean,
    });
    teardownAudioPipeline();
  };

  conn.onmessage = async (event) => {
    if (import.meta.env.DEV) {
      console.debug("[STT][DEV] ElevenLabs mensagem bruta:", event.data);
    }
    try {
      const data = JSON.parse(event.data);
      handleElevenLabsMessage(data, transcriptionSegments, options.onTranscription);
    } catch (error) {
      console.error("[STT] Falha ao processar resposta ElevenLabs", error);
    }
  };

  signal.addEventListener(
    "abort",
    () => {
      console.info("[STT] Abortando conexão ElevenLabs");
      teardownAudioPipeline();
      if (conn.readyState === WebSocket.OPEN || conn.readyState === WebSocket.CONNECTING) {
        conn.close();
      }
    },
    { once: true }
  );

  if (signal.aborted) {
    console.info("[STT] Abort solicitado antes da conexão concluir");
    teardownAudioPipeline();
    if (conn.readyState === WebSocket.OPEN || conn.readyState === WebSocket.CONNECTING) {
      conn.close();
    }
  }
}

async function setupLegacyTranscription({
  signal,
  ...options
}: SetupTranscriptionOptions) {
  const enableSTTService =
    (import.meta.env.VITE_ENABLE_STT_SERVICE ?? "true") !== "false";

  if (!enableSTTService) {
    console.info(
      "[STT] Serviço de transcrição em tempo real desativado (VITE_ENABLE_STT_SERVICE=false)"
    );
  }

  if (enableSTTService) {
    const transcriptionSegments: string[] = [];

    let mediaStream: MediaStream;
    try {
      mediaStream = await options.getAudioStream();
    } catch (error) {
      console.error("[STT] Não foi possível acessar o microfone", error);
      return;
    }

    let audioContext: AudioContext;
    try {
      audioContext = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
    } catch {
      audioContext = new AudioContext();
    }

    try {
      await audioContext.audioWorklet.addModule(PCM_WORKLET_URL);
    } catch (error) {
      console.error("[STT] Falha ao carregar AudioWorklet", error);
      mediaStream.getTracks().forEach((track) => track.stop());
      return;
    }

    const effectiveSampleRate = Math.round(audioContext.sampleRate);
    const speechWsUrl = resolveSpeechWsUrl();
    const targetUrl = `${speechWsUrl}/audio?encoding=linear16&sampleRate=${effectiveSampleRate}`;
    console.info("[STT] Abrindo conexão com serviço em:", targetUrl);
    const conn = new WebSocket(targetUrl);
    conn.binaryType = "arraybuffer";

    let source: MediaStreamAudioSourceNode | null = null;
    let pcmNode: AudioWorkletNode | null = null;
    let silenceGain: GainNode | null = null;
    let audioContextClosed = false;
    let microphoneStopped = false;

    const stopMicrophone = () => {
      if (microphoneStopped) return;
      microphoneStopped = true;
      mediaStream.getTracks().forEach((track) => track.stop());
    };

    const teardownAudioPipeline = () => {
      if (pcmNode) {
        pcmNode.port.onmessage = null;
        pcmNode.disconnect();
        pcmNode = null;
      }
      if (silenceGain) {
        silenceGain.disconnect();
        silenceGain = null;
      }
      if (source) {
        source.disconnect();
        source = null;
      }
      if (!audioContextClosed && audioContext.state !== "closed") {
        audioContextClosed = true;
        audioContext.close().catch((error) => {
          console.warn("[STT] Erro ao encerrar AudioContext", error);
        });
      }
      stopMicrophone();
    };

    conn.onopen = async () => {
      console.info("[STT] Conexão com WebSocket aberta");

      try {
        await audioContext.resume();
      } catch (error) {
        console.warn("[STT] Não foi possível retomar o AudioContext", error);
      }

      source = audioContext.createMediaStreamSource(mediaStream);
      pcmNode = new AudioWorkletNode(audioContext, "pcm-worklet");
      silenceGain = audioContext.createGain();
      silenceGain.gain.value = 0;

      pcmNode.port.onmessage = (event) => {
        if (conn.readyState !== WebSocket.OPEN) return;
        const buffer = event.data as ArrayBuffer;
        if (!buffer) return;
        if (import.meta.env.DEV) {
          logDevRms(new Int16Array(buffer));
        }
        conn.send(buffer);
      };

      source.connect(pcmNode);
      pcmNode.connect(silenceGain);
      silenceGain.connect(audioContext.destination);
      console.info("[STT] Pipeline PCM inicializada", `sampleRate=${audioContext.sampleRate}`);
    };

    conn.onerror = (event) => {
      console.error("[STT] Erro na conexão WebSocket", event);
      teardownAudioPipeline();
    };
    conn.onclose = (event) => {
      console.warn(
        "[STT] Conexão WebSocket encerrada",
        `code=${event.code}`,
        event.reason || "(sem motivo)"
      );
      teardownAudioPipeline();
    };
    conn.onmessage = async (e) => {
      const data = JSON.parse(e.data);
      if (data.message) {
        console.log(
          "[STT] Transcrição parcial:",
          data.message,
          `(isFinal=${data.isFinal}, speechFinal=${data.speechFinal})`
        );
      }
      if (data.message && data.isFinal) {
        transcriptionSegments.push(data.message);
        console.log("[STT] Trecho reconhecido:", transcriptionSegments.join(" "));
      }

      const finalTranscriptionText = transcriptionSegments.join(" ");
      const speechCompleted =
        data.speechFinal === true ||
        (data.speechFinal === undefined && data.isFinal) ||
        (data.isFinal && !data.speechFinal && transcriptionSegments.length > 0);
      if (finalTranscriptionText && speechCompleted) {
        transcriptionSegments.length = 0;
        console.log("[STT] Transcrição final completa:", finalTranscriptionText);
        try {
          await options.onTranscription(finalTranscriptionText);
        } catch (error) {
          console.error("[STT] Falha ao enviar transcrição para a IA", error);
        }
      }
    };

    signal.addEventListener(
      "abort",
      () => {
        console.info("[STT] Abortando conexão com o serviço de transcrição");
        teardownAudioPipeline();
        if (conn.readyState === WebSocket.OPEN || conn.readyState === WebSocket.CONNECTING) {
          conn.close();
        }
      },
      { once: true }
    );

    if (signal.aborted) {
      console.info("[STT] Abort solicitado antes da conexão concluir");
      teardownAudioPipeline();
      if (conn.readyState === WebSocket.OPEN || conn.readyState === WebSocket.CONNECTING) {
        conn.close();
      }
    }
  } else {
    setupNativeRecognition({ ...options, signal });
  }
}

function resolveSpeechWsUrl() {
  const explicit = import.meta.env.VITE_SPEECH_WS_URL;
  const trimmed = explicit?.trim();
  if (trimmed) {
    return trimmed.replace(/\/$/, "");
  }

  if (typeof window === "undefined") {
    return "ws://localhost:5000";
  }

  const url = new URL(window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  if (import.meta.env.DEV) {
    url.port = DEFAULT_STT_DEV_PORT;
  }
  url.pathname = "";
  url.search = "";
  url.hash = "";
  return url.toString().replace(/\/$/, "");
}

async function startPlaybackFromBase64(
  base64: string,
  canvas: HTMLCanvasElement | null
): Promise<PlaybackHandle | null> {
  let audioContext: AudioContext;
  try {
    audioContext = new AudioContext();
  } catch (error) {
    console.error("[STT] Não foi possível inicializar AudioContext", error);
    return null;
  }

  let buffer: AudioBuffer;
  try {
    buffer = await audioContext.decodeAudioData(base64ToArrayBuffer(base64));
  } catch (error) {
    console.error("[STT] Falha ao decodificar áudio da IA", error);
    await audioContext.close().catch(() => {});
    return null;
  }

  const source = audioContext.createBufferSource();
  source.buffer = buffer;
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 2048;
  source.connect(analyser);
  analyser.connect(audioContext.destination);
  source.start();

  console.log("[STT] Reproduzindo áudio da IA (~", buffer.duration.toFixed(2), "s )");
  if (canvas) {
    drawWaveform(canvas, analyser);
  }

  return { context: audioContext, source };
}

function drawWaveform(canvas: HTMLCanvasElement, analyser: AnalyserNode) {
  const canvasCtx = canvas?.getContext("2d");
  if (!canvasCtx) return;

  requestAnimationFrame(() => drawWaveform(canvas, analyser));

  const bufferLength = analyser.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);

  analyser.getByteTimeDomainData(dataArray);

  canvasCtx.fillStyle = "rgb(31 41 55)";
  canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

  canvasCtx.lineWidth = 2;
  canvasCtx.strokeStyle = "rgb(26 175 80)";
  canvasCtx.beginPath();

  const sliceWidth = canvas.width / bufferLength;
  let x = 0;

  for (let i = 0; i < bufferLength; i++) {
    const v = dataArray[i] / 128.0;
    const y = (v * canvas.height) / 2;

    if (i === 0) {
      canvasCtx.moveTo(x, y);
    } else {
      canvasCtx.lineTo(x, y);
    }

    x += sliceWidth;
  }

  canvasCtx.lineTo(canvas.width, canvas.height / 2);
  canvasCtx.stroke();
}

function getSttSetupFn() {
  if (STT_PROVIDER === "browser") {
    console.info("[STT] Usando reconhecimento nativo do navegador (Web Speech API).");
    return setupNativeRecognition;
  }
  if (STT_PROVIDER === "service") return setupLegacyTranscription;
  if (STT_PROVIDER === "elevenlabs" && ELEVENLABS_STT_ENABLED) {
    console.info("[STT] Usando ElevenLabs como provedor de STT.");
    return setupElevenLabsTranscription;
  }
  if (STT_PROVIDER === "elevenlabs" && !ELEVENLABS_STT_ENABLED) {
    console.warn(
      "[STT] Provider configurado para ElevenLabs, mas VITE_ELEVENLABS_STT_ENABLED=false. Usando reconhecimento nativo do navegador."
    );
    return setupNativeRecognition;
  }
  console.warn(
    `[STT] Provider "${STT_PROVIDER}" não reconhecido. Usando reconhecimento nativo do navegador.`
  );
  return setupNativeRecognition;
}

function setupNativeRecognition(options: SetupTranscriptionOptions) {
  const { signal } = options;
  try {
    const win = window as SpeechRecognitionWindow;
    const RecognitionCtor = win.SpeechRecognition || win.webkitSpeechRecognition;
    if (!RecognitionCtor) {
      throw new Error("SpeechRecognition indisponível");
    }
    const recognition = new RecognitionCtor();
    console.info("[STT] Reconhecimento nativo iniciado (pt-BR).");

    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = "pt-BR";

    const restartRecognition = () => {
      try {
        recognition.start();
      } catch (error) {
        if ((error as DOMException).name === "InvalidStateError") {
          recognition.stop();
          setTimeout(() => {
            try {
              recognition.start();
            } catch (startError) {
              console.error("[STT] Falha ao reiniciar reconhecimento nativo:", startError);
            }
          }, 150);
        } else {
          console.error("[STT] Erro ao reiniciar reconhecimento nativo:", error);
        }
      }
    };

    recognition.onresult = async (event: BrowserSpeechRecognitionEvent) => {
      recognition.stop();
      const text = event.results[0][0].transcript.trim();
      if (!text) {
        restartRecognition();
        return;
      }
      console.log("[STT][Nativo] Transcrição final:", text);
      await options.onTranscription(text);
      restartRecognition();
    };
    recognition.onerror = (e: BrowserSpeechRecognitionErrorEvent) => {
      if (e.error === "not-allowed") {
        recognition.stop();
        alert(
          "Essa funcionalidade não é suportada no seu dispositivo ou navegador, entre no desktop para utilizar o serviço de IA."
        );
      } else if (e.error === "aborted") {
        console.info("[STT] Reconhecimento nativo abortado.");
      } else if (e.error === "no-speech" || e.error === "audio-capture") {
        console.warn("[STT] Erro de áudio no reconhecimento nativo:", e.error);
        restartRecognition();
      } else {
        console.warn("[STT] Erro no reconhecimento nativo:", e.error);
        restartRecognition();
      }
    };
    recognition.start();
    signal.addEventListener("abort", () => recognition.abort(), { once: true });
  } catch (error) {
    console.error("[STT] Falha no fallback nativo", error);
    alert(
      "Essa funcionalidade não é suportada no seu dispositivo ou navegador, entre no desktop para utilizar o serviço de IA."
    );
  }
}

function base64ToArrayBuffer(base64: string) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function handleElevenLabsMessage(
  data: Record<string, unknown>,
  transcriptionSegments: string[],
  onFinal: (text: string) => void | Promise<void>
) {
  if (!data) return;

  const messageType = (data.type || data.message_type) as string | undefined;
  const payloadText = typeof data.text === "string" ? data.text : undefined;

  if (messageType === "commit_throttled") {
    console.warn("[STT] ElevenLabs throttled commit:", data.error || data);
    return;
  }

  if (payloadText && messageType?.includes("partial")) {
    console.log("[STT] Transcrição parcial (ElevenLabs):", payloadText);
    return;
  }

  const isCommitted =
    messageType === "committed_transcript" ||
    messageType === "final_transcript" ||
    messageType === "transcription" ||
    data.commit === true;

  if (isCommitted && payloadText) {
    transcriptionSegments.push(payloadText);
    const finalText = transcriptionSegments.join(" ").trim();
    transcriptionSegments.length = 0;
    if (finalText) {
      console.log("[STT] Transcrição final (ElevenLabs):", finalText);
      void onFinal(finalText);
      return;
    }
  }

  if (import.meta.env.DEV) {
    console.debug("[STT][DEV] Mensagem ElevenLabs não reconhecida", data);
  }
}

function messageMatchesIdentifier(
  raw: { identifier?: string } | undefined,
  target: { channel: string; user_test_id?: string | undefined }
) {
  if (!raw?.identifier) return false;
  try {
    const parsed = JSON.parse(raw.identifier);
    return (
      parsed.channel === target.channel &&
      parsed.user_test_id === target.user_test_id
    );
  } catch {
    return false;
  }
}

let accumulatedSamples = 0;
let accumulatedCount = 0;

function logDevRms(chunk: Int16Array) {
  if (!import.meta.env.DEV || chunk.length === 0) return;

  accumulatedCount += chunk.length;
  for (let i = 0; i < chunk.length; i++) {
    const sample = chunk[i] || 0;
    accumulatedSamples += sample * sample;
  }

  if (accumulatedCount >= TARGET_SAMPLE_RATE / 4) {
    const rms = Math.sqrt(accumulatedSamples / accumulatedCount) / 0x7fff;
    console.log("[STT][DEV] RMS médio (250ms):", rms.toFixed(6));
    accumulatedSamples = 0;
    accumulatedCount = 0;
  }
}
